import "@/App.css";
import { registerPreload, installLinkPrefetch } from "./lib/preload";
import { TopProgress } from "./components/TopProgress";
import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { NavTracker } from "./lib/navHistory";
import { AppProvider, useApp } from "./context/AppContext";
import { AuthProvider } from "./context/AuthContext";
import { Layout } from "./components/Layout";
import { SandboxEntry, SandboxBanner } from "./components/Sandbox";
import { isStaffHost } from "./lib/hosts";
import { captureUtm } from "./lib/utm";
import { MetaConsent } from "./components/MetaConsent";
import { useCanonical } from "./lib/useCanonical";
import { useOffice } from "./backoffice/office";
import { PwaManager } from "./components/pwa/PwaManager";

// Route-level code-splitting: only the landing pages, login and the layout shell are in the main bundle.
const Lessons = lazy(() => import("./pages/Lessons"));
const LessonPlay = lazy(() => import("./pages/LessonPlay"));
const Games = lazy(() => import("./pages/Games"));
const FallingWords = lazy(() => import("./pages/FallingWords"));
const TypingRace = lazy(() => import("./pages/TypingRace"));
const OwlAdventures = lazy(() => import("./pages/OwlAdventures"));
const Register = lazy(() => import("./pages/Register"));
const Home = lazy(() => import("./pages/Home"));
const AppLaunch = lazy(() => import("./pages/AppLaunch"));
const Login = lazy(() => import("./pages/Login"));
const StudentReport = lazy(() => import("./components/StudentReport").then((m) => ({ default: m.StudentReport })));
const CustomCursor = lazy(() => import("./components/CustomCursor"));
const StaffLogin = lazy(() => import("./pages/StaffLogin"));
const FamilyLogin = lazy(() => import("./pages/FamilyLogin"));
const FamilyLayout = lazy(() => import("./pages/family/FamilyLayout"));
const FamilyHome = lazy(() => import("./pages/family/FamilyHome"));
const FamilyGroups = lazy(() => import("./pages/family/FamilyGroups"));
const FamilyLessons = lazy(() => import("./pages/family/FamilyLessons"));
const FamilySchedule = lazy(() => import("./pages/family/FamilySchedule"));
const FamilyHomework = lazy(() => import("./pages/family/FamilyHomework"));
const FamilyAttendance = lazy(() => import("./pages/family/FamilyAttendance"));
const fp = (k) => lazy(() => import("./pages/family/FamilyPages").then((m) => ({ default: m[k] })));
const FamilySchool = fp("FamilySchool"), FamilyNotes = fp("FamilyNotes"), FamilyCards = fp("FamilyCards"), FamilyCertificates = fp("FamilyCertificates"), FamilyPayments = fp("FamilyPayments"), FamilyNews = fp("FamilyNews"), FamilyMessages = fp("FamilyMessages"), FamilyAccount = fp("FamilyAccount");
const SharedProject = lazy(() => import("./pages/SharedProject"));
const Community = lazy(() => import("./pages/Community"));
const CommunityReports = lazy(() => import("./backoffice/pages/CommunityReports"));
const Testimonials = lazy(() => import("./backoffice/pages/Testimonials"));
const DemoAccounts = lazy(() => import("./backoffice/pages/DemoAccounts"));
const Mailbox = lazy(() => import("./backoffice/pages/Mailbox"));
const DemoWelcome = lazy(() => import("./pages/DemoWelcome"));
const PaymentsDue = lazy(() => import("./backoffice/pages/PaymentsDue"));
const Expenses = lazy(() => import("./backoffice/pages/Expenses"));
const TeachingPlan = lazy(() => import("./backoffice/pages/TeachingPlan"));
const Ledger = lazy(() => import("./backoffice/pages/Ledger"));
const Permissions = lazy(() => import("./backoffice/pages/Permissions"));
const LearnerDetail = lazy(() => import("./backoffice/pages/LearnerDetail"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const About = lazy(() => import("./pages/About"));
const Terms = lazy(() => import("./pages/Terms"));
const Security = lazy(() => import("./pages/Security"));
const TrialTerms = lazy(() => import("./pages/TrialTerms"));
const CodeTrial = lazy(() => import("./pages/CodeTrial"));
const PythonTrial = lazy(() => import("./pages/PythonTrial"));
const Activity = lazy(() => import("./pages/Activity"));
const PaintPlayground = lazy(() => import("./pages/PaintPlayground"));
const ConductMode = lazy(() => import("./pages/ConductMode"));
const PracticeMode = lazy(() => import("./pages/PracticeMode"));
const LessonEditor = lazy(() => import("./backoffice/pages/LessonEditor"));
const BookCourseEditor = lazy(() => import("./backoffice/pages/BookCourseEditor"));
const Activities = lazy(() => import("./pages/Activities"));
const BookPage = lazy(() => import("./pages/BookPage"));
const KidHome = lazy(() => import("./kid/KidHome"));
const KidHomework = lazy(() => import("./kid/KidPages").then((m) => ({ default: m.KidHomework })));
const KidStars = lazy(() => import("./kid/KidPages").then((m) => ({ default: m.KidStars })));
const KidProjects = lazy(() => import("./kid/KidProjects"));
const PublicSite = lazy(() => import("./pages/PublicSite"));
const CanvasEditor = lazy(() => import("./pages/canvas/CanvasEditor"));
const DesignThinking = lazy(() => import("./pages/DesignThinking"));
const MindMapTrial = lazy(() => import("./pages/MindMapTrial"));
const WhiteboardTrial = lazy(() => import("./pages/WhiteboardTrial"));
const ProjectsReview = lazy(() => import("./backoffice/pages/ProjectsReview"));
const Assignment = lazy(() => import("./pages/Assignment"));
const HowToUse = lazy(() => import("./pages/HowToUse"));
const CursorDemo = lazy(() => import("./pages/CursorDemo"));
const Space3DDemo = lazy(() => import("./pages/Space3DDemo"));
const NeoStartDemo = lazy(() => import("./pages/NeoStartDemo"));
const StudentJoin = lazy(() => import("./pages/StudentJoin"));
const TrainerDashboard = lazy(() => import("./pages/TrainerDashboard"));
const ClassDetail = lazy(() => import("./pages/ClassDetail"));
const LessonHub = lazy(() => import("./pages/LessonHub"));
const TrainerGrade = lazy(() => import("./pages/TrainerGrade"));
const Gradebook = lazy(() => import("./pages/Gradebook"));
const StudyPlan = lazy(() => import("./pages/StudyPlan"));
const GradeStudent = lazy(() => import("./pages/GradeStudent"));
const GradeReportCards = lazy(() => import("./backoffice/pages/GradeReportCards"));
const TrainerTimesheet = lazy(() => import("./pages/TrainerTimesheet"));
const TrainerSchedule = lazy(() => import("./pages/TrainerSchedule"));
const TeacherSchedules = lazy(() => import("./backoffice/pages/TeacherSchedules"));
const SchoolTimetable = lazy(() => import("./backoffice/pages/SchoolTimetable"));
const StaffDetail = lazy(() => import("./backoffice/pages/StaffDetail"));
const Timesheets = lazy(() => import("./backoffice/pages/Timesheets"));
const Tutoring = lazy(() => import("./backoffice/pages/Tutoring"));
const TrainerProfile = lazy(() => import("./pages/TrainerProfile"));
const TrainerTeaching = lazy(() => import("./pages/TrainerTeaching"));
const Wallet = lazy(() => import("./pages/Wallet"));
const MyAccount = lazy(() => import("./pages/MyAccount"));
const LoginCards = lazy(() => import("./pages/LoginCards"));
const BackofficeLayout = lazy(() => import("./backoffice/BackofficeLayout"));
const TrainerLayout = lazy(() => import("./backoffice/TrainerLayout"));
const Overview = lazy(() => import("./backoffice/pages/Overview"));
const Centers = lazy(() => import("./backoffice/pages/Centers"));
const CenterDetail = lazy(() => import("./backoffice/pages/CenterDetail"));
const Offices = lazy(() => import("./backoffice/pages/Offices"));
const FamilyUnits = lazy(() => import("./backoffice/pages/FamilyUnits"));
const FamilyUpdates = lazy(() => import("./backoffice/pages/FamilyUpdates"));
const NameReview = lazy(() => import("./backoffice/pages/NameReview"));
const Learners = lazy(() => import("./backoffice/pages/Learners"));
const Staff = lazy(() => import("./backoffice/pages/Staff"));
const Families = lazy(() => import("./backoffice/pages/Families"));
const Settings = lazy(() => import("./backoffice/pages/Settings"));
const LiveStars = lazy(() => import("./stars/LiveStars"));
const LoginCardsPrint = lazy(() => import("./backoffice/pages/LoginCardsPrint"));
const OrgStaff = lazy(() => import("./backoffice/pages/OrgStaff"));
const Grades = lazy(() => import("./backoffice/pages/Grades"));
const GradeDetail = lazy(() => import("./backoffice/pages/GradeDetail"));
const GradeRegister = lazy(() => import("./pages/GradeRegister"));
const EnterpriseBilling = lazy(() => import("./backoffice/pages/EnterpriseBilling"));
const OrgCurricula = lazy(() => import("./backoffice/pages/OrgCurricula"));
const OrgCurriculumEditor = lazy(() => import("./backoffice/pages/OrgCurriculumEditor"));
const Classes = lazy(() => import("./backoffice/pages/Classes"));
const ClassDetailBo = lazy(() => import("./backoffice/pages/ClassDetailBo"));
const Feedback = lazy(() => import("./backoffice/pages/Feedback"));
const Inquiries = lazy(() => import("./backoffice/pages/Inquiries"));
const Income = lazy(() => import("./backoffice/pages/Income"));
const CodeTrialLeads = lazy(() => import("./backoffice/pages/CodeTrialLeads"));
const Billing = lazy(() => import("./backoffice/pages/Billing"));
const Pricing = lazy(() => import("./pages/Pricing"));
const ParentPlan = lazy(() => import("./pages/ParentPlan"));
const Audit = lazy(() => import("./backoffice/pages/Audit"));
const Bin = lazy(() => import("./backoffice/pages/Bin"));
const Announcements = lazy(() => import("./backoffice/announcements/Announcements"));
const CurriculumAdmin = lazy(() => import("./backoffice/pages/CurriculumAdmin"));
const TeachingGuides = lazy(() => import("./backoffice/pages/TeachingGuides"));
const GuideFigure = lazy(() => import("./components/coding/GuideFigure"));
const AdventureLesson1 = lazy(() => import("./pages/AdventureLesson1"));
const PixelTrial = lazy(() => import("./pages/PixelTrial"));
const Inbox = lazy(() => import("./pages/Inbox"));

// Warm the code chunk of a page when a link to it is hovered / focused (lib/preload.js)
registerPreload("/demo/cursor", () => import("./pages/CursorDemo"));
registerPreload("/demo/space-3d", () => import("./pages/Space3DDemo"));
registerPreload("/backoffice", () => import("./backoffice/BackofficeLayout"));
registerPreload("/backoffice/centers", () => import("./backoffice/pages/Centers"));
registerPreload("/backoffice/offices", () => import("./backoffice/pages/Offices"));
registerPreload("/backoffice/family-units", () => import("./backoffice/pages/FamilyUnits"));
registerPreload("/backoffice/family-updates", () => import("./backoffice/pages/FamilyUpdates"));
registerPreload("/backoffice/name-review", () => import("./backoffice/pages/NameReview"));
registerPreload("/backoffice/community", () => import("./backoffice/pages/CommunityReports"));
registerPreload("/backoffice/payments-due", () => import("./backoffice/pages/PaymentsDue"));
registerPreload("/backoffice/expenses", () => import("./backoffice/pages/Expenses"));
registerPreload("/backoffice/teaching-plan", () => import("./backoffice/pages/TeachingPlan"));
registerPreload("/backoffice/teacher-schedules", () => import("./backoffice/pages/TeacherSchedules"));
registerPreload("/backoffice/school-timetable", () => import("./backoffice/pages/SchoolTimetable"));
registerPreload("/backoffice/my-timesheet", () => import("./pages/TrainerTimesheet"));
registerPreload("/backoffice/wallet", () => import("./pages/Wallet"));
registerPreload("/backoffice/my-account", () => import("./pages/MyAccount"));
registerPreload("/backoffice/permissions", () => import("./backoffice/pages/Permissions"));
registerPreload("/backoffice/learners", () => import("./backoffice/pages/Learners"));
registerPreload("/backoffice/timesheets", () => import("./backoffice/pages/Timesheets"));
registerPreload("/backoffice/projects-review", () => import("./backoffice/pages/ProjectsReview"));
registerPreload("/backoffice/tutoring", () => import("./backoffice/pages/Tutoring"));
registerPreload("/backoffice/families", () => import("./backoffice/pages/Families"));
registerPreload("/backoffice/feedback", () => import("./backoffice/pages/Feedback"));
registerPreload("/backoffice/inquiries", () => import("./backoffice/pages/Inquiries"));
registerPreload("/backoffice/income", () => import("./backoffice/pages/Income"));
registerPreload("/backoffice/code-trial", () => import("./backoffice/pages/CodeTrialLeads"));
registerPreload("/backoffice/billing", () => import("./backoffice/pages/Billing"));
registerPreload("/backoffice/audit", () => import("./backoffice/pages/Audit"));
registerPreload("/backoffice/bin", () => import("./backoffice/pages/Bin"));
registerPreload("/backoffice/announcements", () => import("./backoffice/announcements/Announcements"));
registerPreload("/backoffice/inbox", () => import("./pages/Inbox"));
registerPreload("/backoffice/curriculum", () => import("./backoffice/pages/CurriculumAdmin"));
registerPreload("/backoffice/guides", () => import("./backoffice/pages/TeachingGuides"));
registerPreload("/backoffice/curricula", () => import("./backoffice/pages/OrgCurricula"));
registerPreload("/backoffice/grades", () => import("./backoffice/pages/Grades"));
registerPreload("/backoffice/enterprise-billing", () => import("./backoffice/pages/EnterpriseBilling"));
registerPreload("/backoffice/classes", () => import("./backoffice/pages/Classes"));
registerPreload("/backoffice/settings", () => import("./backoffice/pages/Settings"));
registerPreload("/login", () => import("./pages/Login"));
registerPreload("/kids", () => import("./pages/Home"));
registerPreload("/staff-login", () => import("./pages/StaffLogin"));
registerPreload("/family/login", () => import("./pages/FamilyLogin"));
registerPreload("/family", () => import("./pages/family/FamilyLayout"));
registerPreload("/family/groups", () => import("./pages/family/FamilyGroups"));
registerPreload("/family/lessons", () => import("./pages/family/FamilyLessons"));
registerPreload("/family/schedule", () => import("./pages/family/FamilySchedule"));
registerPreload("/family/homework", () => import("./pages/family/FamilyHomework"));
registerPreload("/family/attendance", () => import("./pages/family/FamilyAttendance"));
registerPreload("/trainer", () => import("./pages/TrainerDashboard"));
registerPreload("/trainer/profile", () => import("./pages/TrainerProfile"));
registerPreload("/trainer/teaching", () => import("./pages/TrainerTeaching"));
registerPreload("/trainer/projects", () => import("./backoffice/pages/ProjectsReview"));
registerPreload("/trainer/wallet", () => import("./pages/Wallet"));
registerPreload("/trainer/timesheet", () => import("./pages/TrainerTimesheet"));
registerPreload("/trainer/schedule", () => import("./pages/TrainerSchedule"));
registerPreload("/trainer/guides", () => import("./backoffice/pages/TeachingGuides"));
registerPreload("/trainer/announcements", () => import("./backoffice/announcements/Announcements"));
registerPreload("/trainer/inbox", () => import("./pages/Inbox"));
registerPreload("/demo/neo-start", () => import("./pages/NeoStartDemo"));
registerPreload("/guide-fig", () => import("./components/coding/GuideFigure"));
registerPreload("/lessons", () => import("./pages/Lessons"));
registerPreload("/games", () => import("./pages/Games"));
registerPreload("/adventures", () => import("./pages/OwlAdventures"));
registerPreload("/about", () => import("./pages/About"));
registerPreload("/terms", () => import("./pages/Terms"));
registerPreload("/security", () => import("./pages/Security"));
registerPreload("/trial-terms", () => import("./pages/TrialTerms"));
registerPreload("/how-to-use", () => import("./pages/HowToUse"));
registerPreload("/pricing", () => import("./pages/Pricing"));
registerPreload("/register", () => import("./pages/Register"));
registerPreload("/forgot-password", () => import("./pages/ForgotPassword"));
registerPreload("/reset-password", () => import("./pages/ResetPassword"));
registerPreload("/projects", () => import("./kid/KidProjects"));
registerPreload("/community", () => import("./pages/Community"));
registerPreload("/account/plan", () => import("./pages/ParentPlan"));
registerPreload("/join", () => import("./pages/StudentJoin"));
registerPreload("/pixel-trial", () => import("./pages/PixelTrial"));
registerPreload("/design-thinking", () => import("./pages/DesignThinking"));
registerPreload("/mind-map", () => import("./pages/MindMapTrial"));
registerPreload("/whiteboard", () => import("./pages/WhiteboardTrial"));
registerPreload("/code-trial", () => import("./pages/CodeTrial"));
registerPreload("/python-trial", () => import("./pages/PythonTrial"));
registerPreload("/paint", () => import("./pages/PaintPlayground"));
registerPreload("/activities", () => import("./pages/Activities"));
registerPreload("/home", () => import("./kid/KidHome"));
registerPreload("/canvas", () => import("./kid/KidProjects"));
registerPreload("/game/falling", () => import("./pages/FallingWords"));
registerPreload("/game/race", () => import("./pages/TypingRace"));
installLinkPrefetch();

// BUMA-wide staff see every enterprise's staff; enterprise/office staff manage their own team
function StaffPage() { const { isWide } = useOffice(); return isWide ? <Staff /> : <OrgStaff />; }

// One deployment, three hosts: login.* → staff entrance; stem.* → the family/kids home; everything else → the EduTech/LMS home.
function HostGate({ children }) {
  if (isStaffHost()) return <Navigate to="/staff-login" replace />;
  return children;
}
// The animated cursor (and its animation library) loads only on marketing pages, on devices with a mouse.
const CURSOR_ROUTES = ["/", "/kids", "/about", "/how-to-use", "/pricing"];
function CursorGate() {
  const { pathname } = useLocation();
  const [fine] = useState(() => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(hover: hover) and (pointer: fine)").matches);
  if (!fine || !CURSOR_ROUTES.includes(pathname)) return null;
  return <Suspense fallback={null}><CustomCursor /></Suspense>;
}
function CanonicalTag() { useCanonical(); return null; }
// Shareable language-prefixed URLs (buma.academy/ar, /ar/pricing, /ar/demo, /en/…): switch the language, then continue to the same path.
function LangPrefix({ lang }) {
  const { setLang } = useApp();
  const params = useParams();
  const loc = useLocation();
  useEffect(() => { setLang(lang); }, [lang, setLang]);
  return <Navigate to={`/${params["*"] || ""}${loc.search}`} replace />;
}
// Ad-friendly demo link (buma.academy/demo?utm_…): opens the homepage with the booking dialog already open, keeping UTM params.
function DemoLink() {
  const loc = useLocation();
  const q = new URLSearchParams(loc.search); q.set("demo", "1");
  return <Navigate to={`/?${q.toString()}`} replace />;
}
// Shown while a lazily-loaded route chunk downloads (code-splitting keeps the first load small).
function RouteFallback() {
  return <div className="min-h-[60vh] grid place-items-center" data-testid="route-loading" aria-busy="true"><div className="w-9 h-9 rounded-full border-4 border-slate-200 border-t-[#0038EB] animate-spin" /></div>;
}

captureUtm();

function App() {
  return (
    <div className="App">
      <AppProvider>
        <AuthProvider>
          <BrowserRouter>
            <TopProgress />
            <CursorGate />
            <SandboxBanner />
            <CanonicalTag />
            <NavTracker />
            <MetaConsent />
            <PwaManager />
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/sandbox" element={<SandboxEntry />} />
              <Route path="/ar/*" element={<LangPrefix lang="ar" />} />
              <Route path="/en/*" element={<LangPrefix lang="en" />} />
              <Route path="/demo" element={<DemoLink />} />
              <Route path="/demo/cursor" element={<CursorDemo />} />
              <Route path="/demo/space-3d" element={<Space3DDemo />} />
              <Route path="/backoffice/login-cards/print" element={<LoginCardsPrint />} />
              <Route path="/backoffice" element={<BackofficeLayout />}>
                <Route index element={<Overview />} />
                <Route path="centers" element={<Centers />} />
                <Route path="centers/:id" element={<CenterDetail />} />
                <Route path="offices" element={<Offices />} />
                <Route path="family-units" element={<FamilyUnits />} />
                <Route path="family-units/:id" element={<FamilyUnits />} />
                <Route path="family-updates" element={<FamilyUpdates />} />
                <Route path="name-review" element={<NameReview />} />
                <Route path="community" element={<CommunityReports />} />
                <Route path="testimonials" element={<Testimonials />} />
                <Route path="demos" element={<DemoAccounts />} />
                <Route path="mailbox" element={<Mailbox />} />
                <Route path="payments-due" element={<PaymentsDue />} />
                <Route path="expenses" element={<Expenses />} />
                <Route path="teaching-plan" element={<TeachingPlan />} />
                <Route path="teacher-schedules" element={<TeacherSchedules />} />
                <Route path="school-timetable" element={<SchoolTimetable />} />
                <Route path="groups-timetable" element={<Navigate to="/backoffice/classes?tab=timetable" replace />} />
                <Route path="staff/:id" element={<StaffDetail />} />
                <Route path="team/:id" element={<StaffDetail buma />} />
                <Route path="my-timesheet" element={<TrainerTimesheet />} />
                <Route path="wallet" element={<Wallet />} />
                <Route path="my-account" element={<MyAccount />} />
                <Route path="ledger/:kind/:id" element={<Ledger />} />
                <Route path="permissions" element={<Permissions />} />
                <Route path="learners/:id" element={<LearnerDetail />} />
                <Route path="learners" element={<Learners />} />
                <Route path="staff" element={<StaffPage />} />
                <Route path="timesheets" element={<Timesheets />} />
                <Route path="projects-review" element={<ProjectsReview />} />
                <Route path="tutoring" element={<Tutoring />} />
                <Route path="families" element={<Families />} />
                <Route path="feedback" element={<Feedback />} />
                <Route path="inquiries" element={<Inquiries />} />
                <Route path="income" element={<Income />} />
                <Route path="code-trial" element={<CodeTrialLeads />} />
                <Route path="report/:id" element={<StudentReport />} />
                <Route path="billing" element={<Billing />} />
                <Route path="*" element={<Navigate to="/backoffice" replace />} />
                <Route path="audit" element={<Audit />} />
                <Route path="bin" element={<Bin />} />
                <Route path="announcements" element={<Announcements />} />
                <Route path="inbox" element={<Inbox />} />
                <Route path="curriculum" element={<CurriculumAdmin />} />
                <Route path="curriculum/:lessonId" element={<LessonEditor />} />
                <Route path="curriculum/:cid/book" element={<BookCourseEditor />} />
                <Route path="trainers" element={<Navigate to="/backoffice/staff" replace />} />
                <Route path="guides" element={<TeachingGuides />} />
                <Route path="curricula" element={<OrgCurricula />} />
                <Route path="curricula/:cid" element={<OrgCurriculumEditor />} />
                <Route path="grades" element={<Grades />} />
                <Route path="enterprise-billing" element={<EnterpriseBilling />} />
                <Route path="grades/:gid" element={<GradeDetail />} />
                <Route path="grades/:gid/study-plan" element={<StudyPlan />} />
                <Route path="grades/:gid/students/:sid" element={<GradeStudent />} />
                <Route path="grades/:gid/report-cards" element={<GradeReportCards />} />
                <Route path="gradebook/:scope/:id" element={<Gradebook />} />
                <Route path="classes" element={<Classes />} />
                <Route path="classes/:id" element={<ClassDetailBo />} />
                <Route path="tutoring/:id" element={<ClassDetailBo />} />
                <Route path="settings" element={<Settings />} />
                <Route path="stars" element={<LiveStars />} />
                <Route path="stars/live" element={<LiveStars />} />
              </Route>
              <Route path="/admin" element={<Navigate to="/backoffice" replace />} />
              <Route path="/center" element={<Navigate to="/backoffice" replace />} />
              <Route path="/admin/orgs/:id" element={<Navigate to="/backoffice/centers" replace />} />
              <Route path="/conduct/:lessonId" element={<ConductMode />} />
              <Route path="/app" element={<AppLaunch />} />
              <Route path="/staff-login" element={<StaffLogin />} />
              <Route path="/demo/:token" element={<DemoWelcome />} />
              <Route path="/family/login" element={<FamilyLogin />} />
              <Route path="/family" element={<FamilyLayout />}>
                <Route index element={<FamilyHome />} />
                <Route path="groups" element={<FamilyGroups />} />
                <Route path="groups/:cid" element={<FamilyGroups />} />
                <Route path="lessons" element={<FamilyLessons />} />
                <Route path="school" element={<FamilySchool />} />
                <Route path="schedule" element={<FamilySchedule />} />
                <Route path="homework" element={<FamilyHomework />} />
                <Route path="attendance" element={<FamilyAttendance />} />
                <Route path="notes" element={<FamilyNotes />} />
                <Route path="cards" element={<FamilyCards />} />
                <Route path="certificates" element={<FamilyCertificates />} />
                <Route path="payments" element={<FamilyPayments />} />
                <Route path="news" element={<FamilyNews />} />
                <Route path="messages" element={<FamilyMessages />} />
                <Route path="account" element={<FamilyAccount />} />
              </Route>
              <Route element={<TrainerLayout />}>
                <Route path="/trainer" element={<TrainerDashboard />} />
                <Route path="/trainer/classes/:id" element={<ClassDetail />} />
                <Route path="/trainer/classes/:id/lessons/:lessonId" element={<LessonHub />} />
                <Route path="/trainer/classes/:id/cards" element={<LoginCards />} />
                <Route path="/trainer/report/:id" element={<StudentReport />} />
                <Route path="/trainer/curriculum" element={<Navigate to="/trainer" replace />} />
                <Route path="/trainer/profile" element={<TrainerProfile />} />
                <Route path="/trainer/teaching" element={<TrainerTeaching />} />
                <Route path="/trainer/projects" element={<ProjectsReview />} />
                <Route path="/trainer/wallet" element={<Wallet />} />
                <Route path="/trainer/stars" element={<LiveStars />} />
                <Route path="/trainer/stars/live" element={<LiveStars />} />
                <Route path="/trainer/grades/:gid" element={<TrainerGrade />} />
                <Route path="/trainer/timesheet" element={<TrainerTimesheet />} />
                <Route path="/trainer/schedule" element={<TrainerSchedule />} />
                <Route path="/trainer/guides" element={<TeachingGuides />} />
                <Route path="/trainer/announcements" element={<Announcements />} />
                <Route path="/trainer/inbox" element={<Inbox />} />
                <Route path="/trainer/gradebook/:scope/:id" element={<Gradebook />} />
                <Route path="/trainer/grades/:gid/study-plan" element={<StudyPlan />} />
                <Route path="/trainer/grades/:gid/students/:sid" element={<GradeStudent />} />
                <Route path="/trainer/grades/:gid/register" element={<GradeRegister />} />
              </Route>
              <Route element={<Layout />}>
                <Route path="/" element={<HostGate><Home /></HostGate>} />
                <Route path="/kids" element={<Home />} />
                <Route path="/demo/neo-start" element={<NeoStartDemo />} />
                {process.env.NODE_ENV === "development" && <Route path="/guide-fig" element={<GuideFigure />} />}
                <Route path="/lessons" element={<Lessons />} />
                <Route path="/lesson/:id" element={<LessonPlay />} />
                <Route path="/games" element={<Games />} />
                <Route path="/adventures" element={<OwlAdventures />} />
                <Route path="/about" element={<About />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/security" element={<Security />} />
                <Route path="/trial-terms" element={<TrialTerms />} />
                <Route path="/how-to-use" element={<HowToUse />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/register" element={<Register />} />
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/projects" element={<KidProjects />} />
                {[1, 2, 3, 4, 5, 6].map((n) => <Route key={n} path={`/courses/buma-adventure-lesson-${n}`} element={<AdventureLesson1 n={n} />} />)}
                <Route path="/community" element={<Community />} />
                <Route path="/community/:id" element={<Community />} />
                <Route path="/account/plan" element={<ParentPlan />} />
                <Route path="/site/:slug" element={<PublicSite />} />
                <Route path="/p/:shareId" element={<SharedProject />} />
                <Route path="/join" element={<StudentJoin />} />
                <Route path="/pixel-trial" element={<PixelTrial />} />
                <Route path="/design-thinking" element={<DesignThinking />} />
                <Route path="/mind-map" element={<MindMapTrial />} />
                <Route path="/whiteboard" element={<WhiteboardTrial />} />
                <Route path="/code-trial" element={<CodeTrial />} />
                <Route path="/python-trial" element={<PythonTrial />} />
                <Route path="/backoffice/classes/:id/lessons/:lessonId" element={<LessonHub />} />
                <Route path="/backoffice/classes/:id/cards" element={<LoginCards />} />
                <Route path="/backoffice/tutoring/:id/lessons/:lessonId" element={<LessonHub />} />
                <Route path="/backoffice/tutoring/:id/cards" element={<LoginCards />} />
                <Route path="/backoffice/grades/:gid/register" element={<GradeRegister />} />
                <Route path="/activity/:lessonId" element={<Activity />} />
                <Route path="/paint" element={<PaintPlayground />} />
                <Route path="/lesson/:lessonId/practice" element={<PracticeMode />} />
                <Route path="/activities" element={<Activities />} />
                <Route path="/book/:cid/:fid" element={<BookPage />} />
                <Route path="/home" element={<KidHome />} />
                <Route path="/homework" element={<KidHomework />} />
                <Route path="/stars" element={<KidStars />} />
                <Route path="/canvas" element={<Navigate to="/projects" replace />} />
                <Route path="/canvas/:id" element={<CanvasEditor />} />
                <Route path="/assignment/:aid" element={<Assignment />} />
                <Route path="/game/falling" element={<FallingWords />} />
                <Route path="/game/race" element={<TypingRace />} />
              </Route>
            </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </AppProvider>
    </div>
  );
}

export default App;
